import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import { downloadExcel } from '../../utils/excelUtils';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            {...rest}
        />
    );
};

const ArticleStatsByType = () => {
    const toast = useRef(null);
    const [isLoading, setIsLoading] = useState(true);

    const [users, setUsers] = useState([]);
    const [articles, setArticles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [jobTitles, setJobTitles] = useState([]);
    const [educationLevels, setEducationLevels] = useState([]);
    const [journalTypes, setJournalTypes] = useState([]);

    const [filterDept, setFilterDept] = useState('');
    const [filterAuthorId, setFilterAuthorId] = useState(null);
    const [filterJournalType, setFilterJournalType] = useState('ALL');
    const [filterJournalCategory, setFilterJournalCategory] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [filterTitle, setFilterTitle] = useState('');
    const [filterDegree, setFilterDegree] = useState('');

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const journalCategories = ["SCIE", "SSCI", "AHCI", "SCOPUS", "ESCI", "Khác"];

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        dept: '',
        userId: '',
        journalType: '',
        journalCategory: '',
        status: '',
        fromDate: null,
        toDate: null,
        title: '',
        degree: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Phiên đăng nhập hết hạn!', life: 3000 });
        setTimeout(() => {
            localStorage.clear();
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const headers = getAuthHeaders();
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};

            const [usersRes, articlesRes, deptsRes, titlesRes, degreesRes, jTypeRes] = await Promise.all([
                fetch(`${baseUrl}/user?mode=advanced_selection`, { headers }),
                fetch(`${baseUrl}/scientificarticlemngt?userId=${currentUser.Id}&isAdmin=true`, { headers }),
                fetch(`${baseUrl}/department`, { headers }),
                fetch(`${baseUrl}/jobtitle`, { headers }),
                fetch(`${baseUrl}/educationlevel`, { headers }),
                fetch(`${baseUrl}/journaltype`, { headers })
            ]);

            if ([usersRes, articlesRes, deptsRes, titlesRes, degreesRes, jTypeRes].some(res => res.status === 401 || res.status === 403)) {
                return handleLogout();
            }

            if (usersRes.ok) setUsers(await usersRes.json());
            if (articlesRes.ok) setArticles(await articlesRes.json());
            if (deptsRes.ok) setDepartments(await deptsRes.json());
            if (titlesRes.ok) setJobTitles(await titlesRes.json());
            if (degreesRes.ok) setEducationLevels(await degreesRes.json());
            if (jTypeRes.ok) setJournalTypes(await jTypeRes.json());

        } catch (error) {
            console.error("Lỗi tải dữ liệu:", error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu thống kê!' });
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const labelStr = `${u.LastName || ''} ${u.FirstName || ''} - ${u.Email || ''}`.trim();
            return {
                label: labelStr,
                searchKey: superCleanString(labelStr),
                value: u.Id
            };
        });
    }, [users]);

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const computeAggregatedData = useCallback((filters) => {
        if (!articles.length) return [];

        let allowedDeptIds = [];
        let allowedDeptNames = [];

        if (filters.dept) {
            const selectedDept = departments.find(d => d.Id.toString() === filters.dept.toString());
            if (selectedDept) {
                allowedDeptIds.push(selectedDept.Id.toString());
                allowedDeptNames.push(selectedDept.Name.trim());

                const findChildren = (parentId) => {
                    const children = departments.filter(d => d.ParentId?.toString() === parentId.toString());
                    children.forEach(child => {
                        const childId = child.Id.toString();
                        if (!allowedDeptIds.includes(childId)) {
                            allowedDeptIds.push(childId);
                            allowedDeptNames.push(child.Name.trim());
                            findChildren(childId);
                        }
                    });
                };
                findChildren(selectedDept.Id);
            }
        }

        let statsMap = new Map();

        articles.forEach(article => {
            if (filters.status && article.Status !== filters.status) return;
            if (filters.journalType && filters.journalType !== 'ALL' && article.JournalTypeName !== filters.journalType && article.JournalType !== filters.journalType) return;
            if (filters.journalCategory && article.JournalCategory !== filters.journalCategory) return;

            if (article.PublicationDate) {
                const pubDate = new Date(article.PublicationDate);
                pubDate.setHours(0, 0, 0, 0);

                if (filters.fromDate) {
                    const filterFrom = new Date(filters.fromDate);
                    filterFrom.setHours(0, 0, 0, 0);
                    if (pubDate < filterFrom) return;
                }
                if (filters.toDate) {
                    const filterTo = new Date(filters.toDate);
                    filterTo.setHours(23, 59, 59, 999);
                    if (pubDate > filterTo) return;
                }
            } else if (filters.fromDate || filters.toDate) {
                return;
            }

            let members = [];
            try { members = JSON.parse(article.MembersJSON || '[]'); }
            catch (e) { members = []; }

            const hasAuthorFilter = filters.title || filters.degree || filters.authorId || filters.userId;
            let isValidByAuthor = true;

            if (hasAuthorFilter) {
                const targetId = filters.authorId || filters.userId;

                isValidByAuthor = members.some(member => {
                    const mId = member.UserId || member.userId || member.Id || member.id;
                    if (!mId) return false;
                    const userObj = users.find(u => u.Id.toString() === mId.toString());
                    if (!userObj) return false;

                    let isMatch = true;
                    if (filters.title && userObj.AcademicTitleId?.toString() !== filters.title.toString()) isMatch = false;
                    if (filters.degree && userObj.DegreeId?.toString() !== filters.degree.toString()) isMatch = false;

                    if (targetId && isMatch) {
                        if (userObj.Id.toString() !== targetId.toString()) isMatch = false;
                    }
                    return isMatch;
                });

                if (!isValidByAuthor && article.UserId) {
                    const ownerObj = users.find(u => u.Id.toString() === article.UserId.toString());
                    if (ownerObj) {
                        let isMatch = true;
                        if (filters.title && ownerObj.AcademicTitleId?.toString() !== filters.title.toString()) isMatch = false;
                        if (filters.degree && ownerObj.DegreeId?.toString() !== filters.degree.toString()) isMatch = false;
                        if (targetId && isMatch) {
                            if (ownerObj.Id.toString() !== targetId.toString()) isMatch = false;
                        }
                        isValidByAuthor = isMatch;
                    }
                }
            }

            if (!isValidByAuthor) return;

            if (filters.dept) {
                let matchDept = false;

                if (members.length > 0) {
                    matchDept = members.some(member => {
                        const mId = member.UserId || member.userId || member.Id || member.id;
                        if (!mId) return false;
                        const userObj = users.find(u => u.Id.toString() === mId.toString());
                        if (!userObj) return false;
                        return allowedDeptIds.includes(userObj.DepartmentId?.toString()) ||
                            (userObj.DepartmentName && allowedDeptNames.includes(userObj.DepartmentName.trim()));
                    });
                }

                if (!matchDept) {
                    const itemDeptId = article.DepartmentId ? article.DepartmentId.toString() : null;
                    const itemDeptName = article.DepartmentName ? article.DepartmentName.trim() : "";

                    if (itemDeptId && allowedDeptIds.includes(itemDeptId)) matchDept = true;
                    else if (itemDeptName && allowedDeptNames.includes(itemDeptName)) matchDept = true;
                }

                if (!matchDept) return;
            }

            const jType = article.JournalType || article.JournalTypeName || 'Khác';
            const jCat = article.JournalCategory || '';
            const aType = article.ArticleTypeName || 'Bài báo';

            const key = `${jType}|${jCat}|${aType}`;

            if (!statsMap.has(key)) {
                statsMap.set(key, {
                    journalType: jType,
                    journalCategory: jCat,
                    articleType: aType,
                    count: 0
                });
            }
            statsMap.get(key).count += 1;
        });

        let result = Array.from(statsMap.values());
        result.sort((a, b) => {
            if (a.journalType < b.journalType) return -1;
            if (a.journalType > b.journalType) return 1;
            if (a.articleType < b.articleType) return -1;
            if (a.articleType > b.articleType) return 1;
            return b.count - a.count;
        });

        return result;

    }, [articles, users, departments]);

    const aggregatedData = useMemo(() => computeAggregatedData({
        dept: filterDept,
        authorId: filterAuthorId,
        journalType: filterJournalType,
        journalCategory: filterJournalCategory,
        status: filterStatus,
        fromDate: fromDate,
        toDate: toDate,
        title: filterTitle,
        degree: filterDegree
    }), [computeAggregatedData, filterDept, filterAuthorId, filterJournalType, filterJournalCategory, filterStatus, fromDate, toDate, filterTitle, filterDegree]);

    const journalTypeOptions = useMemo(() => {
        const opts = journalTypes.map(jt => ({
            label: jt.Name,
            value: jt.Name,
            searchKey: superCleanString(jt.Name)
        }));

        return [
            { label: 'Tất cả loại tạp chí', value: 'ALL', searchKey: '' },
            ...opts
        ];
    }, [journalTypes]);

    const executeExport = useCallback((dataToExport, fileSuffix) => {
        if (!dataToExport || dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        let totalArticles = 0;
        const formattedData = dataToExport.map((item, index) => {
            totalArticles += item.count;
            return {
                'STT': index + 1,
                'Loại tạp chí': item.journalType,
                'Danh mục tạp chí': item.journalCategory,
                'Loại bài báo': item.articleType,
                'Số lượng': item.count
            };
        });

        const summaryRow = {
            'STT': '',
            'Loại tạp chí': 'TỔNG CỘNG',
            'Danh mục tạp chí': '',
            'Loại bài báo': `${dataToExport.length} Phân loại`,
            'Số lượng': totalArticles
        };

        const colWidths = [
            { wch: 8 },
            { wch: 30 },
            { wch: 25 },
            { wch: 30 },
            { wch: 15 }
        ];

        downloadExcel({
            data: formattedData,
            fileName: `ThongKeBaiBaoTheoLoai_${fileSuffix}`,
            sheetName: 'ThongKeLoaiBaiBao',
            colWidths: colWidths,
            summaryRow: summaryRow
        });
    }, []);

    const handleExportCurrent = () => {
        executeExport(aggregatedData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const handlePreviewAdvancedFilters = () => {
        const advancedData = computeAggregatedData(exportFilters);
        setPreviewExportData(advancedData);
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ dept: '', userId: '', journalType: '', journalCategory: '', status: '', fromDate: null, toDate: null, title: '', degree: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        const dataToExport = previewExportData || computeAggregatedData(exportFilters);
        executeExport(dataToExport, 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const renderDeptTree = (parentId = null, level = 0) => {
        const targetNodes = parentId === null
            ? departments.filter(d => !d.ParentId || !departments.some(p => p.Id === d.ParentId))
            : departments.filter(d => d.ParentId === parentId);

        return targetNodes.map(d => (
            <React.Fragment key={`filter-dept-${d.Id}`}>
                <option value={d.Id}>
                    {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                </option>
                {renderDeptTree(d.Id, level + 1)}
            </React.Fragment>
        ));
    };

    return (
        <div className="page-container" style={{ padding: '20px' }}>
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header" style={{ marginBottom: '20px' }}>
                <h2 style={{ textTransform: 'uppercase' }}>THỐNG KÊ BÀI BÁO THEO LOẠI</h2>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px' }}>
                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
                <p className="sub-title" style={{ margin: '0 0 15px 0' }}>BỘ LỌC HIỂN THỊ</p>

                <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>

                    <select className="form-input" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
                        <option value="">Chọn đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilterAuthorId(e.value.value);
                                } else {
                                    setFilterAuthorId(null);
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <div style={{ width: '100%' }}>
                        <SearchableFocusDropdown
                            value={filterJournalType}
                            options={journalTypeOptions}
                            onChange={(e) => setFilterJournalType(e.value)}
                            placeholder="Tất cả loại tạp chí"
                            panelUniqueClass="dropdown-panel-filter-journal-type-stats"
                            className="form-input"
                            style={{ width: '100%', height: '40px', display: 'flex', alignItems: 'center' }}
                            panelStyle={{ maxWidth: '400px', width: 'auto', minWidth: '100%' }}
                            itemTemplate={(option) => (
                                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', padding: '4px 0', lineHeight: '1.4' }}>
                                    {option.label}
                                </div>
                            )}
                        />
                    </div>

                    <select className="form-input" value={filterJournalCategory} onChange={(e) => setFilterJournalCategory(e.target.value)}>
                        <option value="">Danh mục tạp chí</option>
                        {journalCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>

                    <select className="form-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="">Trạng thái bài báo</option>
                        <option value="Lưu nháp">Lưu nháp</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                    </select>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.value)}
                            placeholder="Từ ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            showClear
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                            appendTo={document.body}
                        />
                    </div>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={toDate}
                            onChange={(e) => setToDate(e.value)}
                            placeholder="Đến ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            showClear
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                            appendTo={document.body}
                        />
                    </div>
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
                {isLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Đang tải dữ liệu</div>
                ) : (
                    <table className="custom-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px', margin: 0 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr style={{ backgroundColor: '#f4f6f9', borderBottom: '2px solid #ddd' }}>
                                <th style={thStyle}>STT</th>
                                <th style={{ ...thStyle, textAlign: 'left' }}>LOẠI TẠP CHÍ</th>
                                <th style={{ ...thStyle, textAlign: 'left' }}>DANH MỤC TẠP CHÍ</th>
                                <th style={{ ...thStyle, textAlign: 'left' }}>LOẠI BÀI BÁO</th>
                                <th style={thStyle}>SỐ LƯỢNG</th>
                            </tr>
                        </thead>
                        <tbody>
                            {aggregatedData.length > 0 ? (
                                aggregatedData.map((item, index) => (
                                    <tr key={`${item.journalType}-${item.journalCategory}-${item.articleType}`} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={tdStyle}>{index + 1}</td>
                                        <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '500', color: '#333' }}>{item.journalType}</td>
                                        <td style={{ ...tdStyle, textAlign: 'left', color: '#666' }}>{item.journalCategory}</td>
                                        <td style={{ ...tdStyle, textAlign: 'left' }}>{item.articleType}</td>
                                        <td style={{ ...tdStyle, fontWeight: 'bold', color: '#1a56db' }}>{item.count}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#888' }}>Không có dữ liệu bài báo phù hợp với bộ lọc</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 900 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>

                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lọc theo Đơn vị</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.dept} onChange={(e) => setExportFilters({ ...exportFilters, dept: e.target.value })}>
                                        <option value="">Toàn bộ đơn vị</option>
                                        {renderDeptTree()}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Giảng viên</label>
                                    <SearchableFocusDropdown
                                        value={exportFilters.userId}
                                        options={userOptions}
                                        onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                        placeholder="Toàn bộ giảng viên"
                                        panelUniqueClass="custom-dropdown-panel-articlestatstype-export-advanced-filter"
                                        className="custom-dropdown"
                                        filterBy="label,searchKey"
                                        optionLabel="label"
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Loại tạp chí</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.journalType} onChange={(e) => setExportFilters({ ...exportFilters, journalType: e.target.value })}>
                                        <option value="">Tất cả</option>
                                        {journalTypes.map(jt => <option key={`exp-jt-${jt.Id}`} value={jt.Name}>{jt.Name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Danh mục tạp chí</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.journalCategory} onChange={(e) => setExportFilters({ ...exportFilters, journalCategory: e.target.value })}>
                                        <option value="">Tất cả</option>
                                        {journalCategories.map(cat => <option key={`exp-jc-${cat}`} value={cat}>{cat}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Trạng thái Bài báo</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.status} onChange={(e) => setExportFilters({ ...exportFilters, status: e.target.value })}>
                                        <option value="">Tất cả</option>
                                        <option value="Lưu nháp">Lưu nháp</option>
                                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                                        <option value="Đã duyệt">Đã duyệt</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày</label>
                                    <Calendar
                                        value={exportFilters.fromDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, fromDate: e.value })}
                                        placeholder="Từ ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày</label>
                                    <Calendar
                                        value={exportFilters.toDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, toDate: e.value })}
                                        placeholder="Đến ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        showClear
                                        style={{ width: '100%' }}
                                        inputStyle={{ paddingLeft: '12px', padding: '8px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lọc theo Chức danh</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.title} onChange={(e) => setExportFilters({ ...exportFilters, title: e.target.value })}>
                                        <option value="">Toàn bộ chức danh</option>
                                        {jobTitles.map(t => <option key={`exp-t-${t.Id}`} value={t.Id}>{t.Name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lọc theo Trình độ</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.degree} onChange={(e) => setExportFilters({ ...exportFilters, degree: e.target.value })}>
                                        <option value="">Toàn bộ trình độ</option>
                                        {educationLevels.map(d => <option key={`exp-d-${d.Id}`} value={d.Id}>{d.Name}</option>)}
                                    </select>
                                </div>
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu xuất:
                                        ({previewExportData.length} phân loại)
                                    </p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '600px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th style={{ textAlign: 'left' }}>LOẠI TẠP CHÍ</th>
                                                    <th style={{ textAlign: 'left' }}>DANH MỤC</th>
                                                    <th style={{ textAlign: 'left' }}>LOẠI BÀI BÁO</th>
                                                    <th width="80px" style={{ textAlign: 'center' }}>SỐ LƯỢNG</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((row, idx) => (
                                                        <tr key={`preview-${row.journalType}-${row.journalCategory}-${row.articleType}`}>
                                                            <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                            <td style={{ textAlign: 'left' }}>{row.journalType}</td>
                                                            <td style={{ textAlign: 'left' }}>{row.journalCategory}</td>
                                                            <td style={{ textAlign: 'left' }}>{row.articleType}</td>
                                                            <td style={{ textAlign: 'center', color: '#1a56db', fontWeight: 'bold' }}>{row.count}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                                            Không có dữ liệu khớp với bộ lọc
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const thStyle = { padding: '15px 12px', textAlign: 'center', color: '#1a56db', fontSize: '13px', fontWeight: 'bold' };
const tdStyle = { padding: '15px 12px', textAlign: 'center', fontSize: '13px', color: '#333' };

export default ArticleStatsByType;